/**
 * Priorities Management Page
 *
 * CRUD for study priorities (ADMIN/SUPER_ADMIN only)
 * Allows creating, editing, reordering, and toggling priorities
 */

import { useState, useMemo, useEffect } from 'react'
import {
  Flag,
  Plus,
  Search,
  Power,
  PowerOff,
  Trash2,
  Edit,
  GripVertical,
  Loader2,
  AlertCircle,
  Check,
  Star,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import {
  useAllPriorities,
  useCreatePriority,
  useUpdatePriority,
  useDeletePriority,
  useSetDefaultPriority,
} from '@/application/priorities/usePriorities'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Priority, CreatePriorityData, UpdatePriorityData } from '@/domain/priority/types'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'active', label: 'Activas' },
  { value: 'inactive', label: 'Inactivas' },
]

// =============================================================================
// Priority Form Modal
// =============================================================================

interface PriorityFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  priority?: Priority | null
  onSuccess?: () => void
}

function PriorityFormModal({
  open,
  onOpenChange,
  priority,
  onSuccess,
}: PriorityFormModalProps) {
  const isEditing = !!priority

  const [code, setCode] = useState(priority?.code || '')
  const [name, setName] = useState(priority?.name || '')
  const [color, setColor] = useState(priority?.color || '#6B7280')
  const [textColor, setTextColor] = useState(priority?.textColor || '#FFFFFF')
  const [sortOrder, setSortOrder] = useState(priority?.sortOrder?.toString() || '')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreatePriority()
  const updateMutation = useUpdatePriority()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!code.trim()) {
      setError('El código es requerido')
      return
    }

    if (!name.trim()) {
      setError('El nombre es requerido')
      return
    }

    if (!sortOrder || parseInt(sortOrder, 10) < 1) {
      setError('El orden debe ser un número mayor a 0')
      return
    }

    try {
      if (isEditing && priority) {
        const updateData: UpdatePriorityData = {
          name: name.trim(),
          color: color,
          textColor: textColor,
          sortOrder: parseInt(sortOrder, 10),
        }
        await updateMutation.mutateAsync({ id: priority.id, data: updateData })
        toast.success('Prioridad actualizada correctamente')
      } else {
        const createData: CreatePriorityData = {
          code: code.trim().toUpperCase(),
          name: name.trim(),
          color: color,
          textColor: textColor,
          sortOrder: parseInt(sortOrder, 10),
        }
        await createMutation.mutateAsync(createData)
        toast.success('Prioridad creada correctamente')
      }

      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al guardar la prioridad')
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setCode('')
    setName('')
    setColor('#6B7280')
    setTextColor('#FFFFFF')
    setSortOrder('')
    setError(null)
    onOpenChange(false)
  }

  // Reset form when priority changes or modal opens
  useEffect(() => {
    if (open && priority) {
      setCode(priority.code)
      setName(priority.name)
      setColor(priority.color)
      setTextColor(priority.textColor)
      setSortOrder(priority.sortOrder?.toString() || '')
    } else if (open && !priority) {
      // Reset for create mode
      setCode('')
      setName('')
      setColor('#6B7280')
      setTextColor('#FFFFFF')
      setSortOrder('')
    }
    setError(null)
  }, [open, priority])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Prioridad' : 'Nueva Prioridad'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique los datos de la prioridad'
              : 'Complete los datos para crear una nueva prioridad'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ej: STAT, URGENT, ROUTINE"
              maxLength={20}
              disabled={isEditing}
            />
            {isEditing && (
              <p className="text-xs text-[var(--foreground-muted)] mt-1">
                El código no se puede cambiar
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Emergencia (STAT)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Color de fondo
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded border border-[var(--border)] cursor-pointer"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#FF0000"
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Color de texto
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-10 h-10 rounded border border-[var(--border)] cursor-pointer"
                />
                <Input
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  placeholder="#FFFFFF"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Vista previa
            </label>
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800/50 flex items-center gap-3">
              <span
                className="px-3 py-1.5 text-sm font-medium rounded-full"
                style={{ backgroundColor: color, color: textColor }}
              >
                {name || 'Nombre de la prioridad'}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Orden <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="1, 2, 3..."
              min={1}
            />
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Determina el orden en listas y la importancia (1 = mayor urgencia)
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isEditing ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  {isEditing ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Crear
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  priority: Priority | null
  onConfirm: () => void
  isPending: boolean
}

function DeleteModal({
  open,
  onOpenChange,
  priority,
  onConfirm,
  isPending,
}: DeleteModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Eliminar Prioridad</DialogTitle>
          <DialogDescription>
            ¿Está seguro que desea eliminar la prioridad <strong>{priority?.name}</strong> ({priority?.code})?
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 mt-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">
            Los estudios existentes con esta prioridad no serán afectados.
          </span>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function PrioritiesPage() {
  // State
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingPriority, setEditingPriority] = useState<Priority | null>(null)
  const [deletingPriority, setDeletingPriority] = useState<Priority | null>(null)

  // Queries
  const { data: priorities, isLoading } = useAllPriorities()

  const updateMutation = useUpdatePriority()
  const deleteMutation = useDeletePriority()
  const setDefaultMutation = useSetDefaultPriority()

  // Filter priorities by search and status
  const filteredPriorities = useMemo(() => {
    if (!priorities) return []

    let result = [...priorities]

    // Filter by status
    if (statusFilter === 'active') {
      result = result.filter((p) => p.isActive)
    } else if (statusFilter === 'inactive') {
      result = result.filter((p) => !p.isActive)
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(
        (p) =>
          p.code.toLowerCase().includes(searchLower) ||
          p.name.toLowerCase().includes(searchLower)
      )
    }

    // Sort by sortOrder
    result.sort((a, b) => a.sortOrder - b.sortOrder)

    return result
  }, [priorities, search, statusFilter])

  // Handlers
  const handleToggle = (priority: Priority) => {
    updateMutation.mutate(
      { id: priority.id, data: { isActive: !priority.isActive } },
      {
        onSuccess: () => {
          toast.success(priority.isActive ? 'Prioridad desactivada' : 'Prioridad activada')
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al cambiar estado de la prioridad'))
        },
      }
    )
  }

  const handleSetDefault = (priority: Priority) => {
    if (priority.isDefault) return

    setDefaultMutation.mutate(priority.id, {
      onSuccess: () => {
        toast.success(`"${priority.name}" es ahora la prioridad por defecto`)
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al establecer como predeterminada'))
      },
    })
  }

  const handleDelete = () => {
    if (deletingPriority) {
      deleteMutation.mutate(deletingPriority.id, {
        onSuccess: () => {
          toast.success('Prioridad eliminada correctamente')
          setDeletingPriority(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar la prioridad'))
        },
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Prioridades</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestión de prioridades de estudios
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nueva Prioridad
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                placeholder="Buscar por código o nombre..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>

            <div className="w-[150px]">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>

            <div className="text-sm text-[var(--foreground-muted)] ml-auto">
              {filteredPriorities.length} prioridades
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[180px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPriorities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Flag className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                      <p className="mt-2 text-[var(--foreground-muted)]">
                        No hay prioridades
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPriorities.map((priority, index) => (
                    <TableRow key={priority.id}>
                      <TableCell className="text-[var(--foreground-muted)]">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 opacity-30" />
                          {index + 1}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {priority.code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{priority.name}</span>
                          {priority.isDefault && (
                            <span title="Por defecto">
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
                          style={{ backgroundColor: priority.color, color: priority.textColor }}
                        >
                          {priority.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={priority.isActive ? 'success' : 'secondary'}>
                          {priority.isActive ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingPriority(priority)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(priority)}
                            title={priority.isDefault ? 'Es la predeterminada' : 'Establecer como predeterminada'}
                            disabled={priority.isDefault || setDefaultMutation.isPending}
                          >
                            <Star className={`h-4 w-4 ${priority.isDefault ? 'text-yellow-500 fill-yellow-500' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(priority)}
                            title={priority.isActive ? 'Desactivar' : 'Activar'}
                            disabled={updateMutation.isPending}
                          >
                            {priority.isActive ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingPriority(priority)}
                            title="Eliminar"
                            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                            disabled={priority.isDefault}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <PriorityFormModal
        open={showCreateModal || !!editingPriority}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false)
            setEditingPriority(null)
          }
        }}
        priority={editingPriority}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={!!deletingPriority}
        onOpenChange={(open) => !open && setDeletingPriority(null)}
        priority={deletingPriority}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
